import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  GRANDEZAS, ZONAS_SBTN, ZONAS_SBTN_MOD, ZONAS_SCHNEIDER,
  type ColunaDoMotor, type Grandeza, type Leituras, type Resultado,
} from '../../cptu/motor';
import {
  GRUPOS_DA_LEITURA, MARCA_NAO_HOMOLOGADA, SEM_VALOR, leituras, mapaDeIndices, marcaDaColuna, numeroBr, textoDoValor,
} from '../../cptu/visual';
import { plural } from '../../formato';
import { COLUNAS_CORRELACIONADAS, type Parametros } from './correlacionados';

/**
 * A tabela completa da vertical — onda 313, Tarefa 313.4 (spec 07 §6.1, item 4; R7, R9).
 *
 * Uma linha por leitura DO BRUTO, na ordem do arquivo: a profundidade, e o qc, o fs e o u₂ como foram lidos
 * (o que o equipamento gravou, RN-253). As colunas calculadas vêm do `Resultado` pelo mapa entre o bruto e o
 * resultado (`mapaDeIndices`): a leitura que a edição tirou (o pré-furo, um pico) fica na tabela, riscada,
 * com o motivo e sem as calculadas. As colunas se escolhem, cada uma com a unidade e a marca de
 * homologação (R7: «▲ não homologada», e o porquê numa nota numerada embaixo); a porosidade não aparece
 * (não confere com o gabarito).
 *
 * A tabela é a da casa (P-UI-12: a mesma altura de linha das outras, sem modo compacto; o cabeçalho preso
 * da `.rola.congelada`) e desenha só a janela de linhas à vista (R9): a caixa rola por dentro, a altura da
 * linha se mede na primeira desenhada, e o `aria-rowcount` diz quantas leituras há. Clicar numa linha — ou
 * Enter na profundidade dela, que é um botão — leva o cursor a ela (as trilhas e a leitura na cota andam
 * juntas); a linha do cursor leva `aria-current`. Quando o cursor aparece pela primeira vez (a tabela aberta
 * direto, o F5), a tabela vai até ele.
 *
 * Onda 315 (Tarefa 315.5b; o pedido do Humberto de 12/10/2026, como o CPeT-IT): a tabela tem dois modos. Nos
 * «Parâmetros básicos», as colunas de Medidos · Corrigidos e normalizados · Classificação; nos «Parâmetros
 * correlacionados», a profundidade, o qc, o fs e o SBTn fixos à esquerda (presos ao rolar de lado) e as correlações
 * dos grupos (`COLUNAS_CORRELACIONADAS`). Cada modo guarda a escolha das colunas dele neste aparelho.
 */

/** O que muda entre os dois modos: as colunas que se escolhem, as do começo e onde a escolha fica guardada. */
const MODOS: Readonly<Record<Parametros, { escolhiveis: readonly ColunaDoMotor[]; padrao: readonly ColunaDoMotor[]; chave: string }>> = {
  basicos: {
    escolhiveis: GRUPOS_DA_LEITURA.filter((g) => g.titulo !== 'Medidos' && g.titulo !== 'Correlações').flatMap((g) => g.colunas),
    padrao: ['qt', 'rf', 'gama', 'sv', 'u0', 'svl', 'qtn', 'fr', 'ic', 'sbtn'],
    chave: 'labmanager.cptu.colunas',
  },
  correlacionados: {
    escolhiveis: COLUNAS_CORRELACIONADAS,
    padrao: COLUNAS_CORRELACIONADAS,
    chave: 'labmanager.cptu.colunasCorrelacionadas',
  },
};

/**
 * A altura da linha antes da primeira medida (px): a da tabela da casa (`.9rem` com 1,45 de entrelinha e
 * `.6rem` em cima e embaixo, mais o fio). Quantas linhas a mais se desenham acima e abaixo da janela.
 */
const ALTURA_INICIAL_DA_LINHA = 41;
const FOLGA = 15;

const ehZona = (c: ColunaDoMotor): c is 'sbt' | 'sbtn' | 'sbtnMod' | 'schneider' =>
  c === 'sbt' || c === 'sbtn' || c === 'sbtnMod' || c === 'schneider';

const ROTULO_DA_ZONA: Readonly<Record<'sbt' | 'sbtn' | 'sbtnMod' | 'schneider', string>> = {
  sbt: 'SBT', sbtn: 'SBTn', sbtnMod: 'SBTn mod.', schneider: 'Schneider',
};

/** O cabeçalho de uma coluna: o rótulo e a unidade. */
const cabecaDe = (c: ColunaDoMotor): string => {
  if (ehZona(c)) return ROTULO_DA_ZONA[c];
  const g = GRANDEZAS[c];
  return g.unidade ? `${g.rotulo} (${g.unidade})` : g.rotulo;
};

/** A zona escrita, curta: «7 · areia densa», «7 · SD», «3 · 1a» — a cor nunca vai sozinha, nem aqui. */
function zonaCurta(c: 'sbt' | 'sbtn' | 'sbtnMod' | 'schneider', zona: number): string {
  if (c === 'sbtnMod') {
    const z = (ZONAS_SBTN_MOD as Readonly<Record<number, { sigla: string }>>)[zona];
    return z ? `${zona} · ${z.sigla}` : SEM_VALOR;
  }
  if (c === 'schneider') {
    const z = ZONAS_SCHNEIDER[zona];
    return z ? z.codigo : SEM_VALOR;
  }
  const z = (ZONAS_SBTN as Readonly<Record<number, { nomeCurto: string }>>)[zona];
  return z ? `${zona} · ${z.nomeCurto}` : SEM_VALOR;
}

function lerColunas(modo: Parametros): ColunaDoMotor[] {
  const m = MODOS[modo];
  try {
    const salvas = JSON.parse(localStorage.getItem(m.chave) ?? 'null');
    if (Array.isArray(salvas)) {
      const validas = salvas.filter((c): c is ColunaDoMotor => m.escolhiveis.includes(c));
      if (validas.length > 0) return validas;
    }
  } catch { /* o aparelho não guarda: fica o padrão */ }
  return [...m.padrao];
}

function gravarColunas(modo: Parametros, colunas: readonly ColunaDoMotor[]): void {
  try { localStorage.setItem(MODOS[modo].chave, JSON.stringify(colunas)); } catch { /* nada */ }
}

export function TabelaDaVertical({ bruto, r, ordemDoCursor, aoEscolher, modo = 'basicos' }: {
  bruto: Leituras;
  r: Resultado;
  /** A leitura do bruto em que o cursor está (destacada). */
  ordemDoCursor: number | null;
  aoEscolher(ordem: number): void;
  /** Os parâmetros básicos ou os correlacionados (onda 315); quem troca o modo remonta a tabela. */
  modo?: Parametros;
}) {
  const nBruto = bruto.profundidade.length;
  const correlacionados = modo === 'correlacionados';
  const COLUNAS_ESCOLHIVEIS = MODOS[modo].escolhiveis;
  const [colunas, setColunas] = useState<ColunaDoMotor[]>(() => lerColunas(modo));
  /** Nos correlacionados, onde cada uma das quatro colunas fixas fica presa (o deslocamento dela na tabela). */
  const [esquerdas, setEsquerdas] = useState<number[]>([]);
  const cabeca = useRef<HTMLTableRowElement>(null);
  const [escolhendo, setEscolhendo] = useState(false);
  const caixa = useRef<HTMLDivElement>(null);
  const corpo = useRef<HTMLTableSectionElement>(null);
  const [alturaDaLinha, setAlturaDaLinha] = useState(ALTURA_INICIAL_DA_LINHA);
  const [janela, setJanela] = useState({ topo: 0, altura: 560 });
  /** A altura com que a tabela centrou no cursor, e se já centrou de novo com a medida. */
  const centrou = useRef<{ altura: number; deNovo: boolean } | null>(null);
  const mapa = useMemo(() => mapaDeIndices(r, nBruto), [r, nBruto]);
  const motivoDa = useMemo(() => new Map(r.removidas.map((x) => [x.ordem, x.motivo])), [r]);

  const visiveis = colunas.filter((c) => marcaDaColuna(c, r.premissas).mostrar);
  // As marcas de cada coluna (com as premissas da interpretação) e os porquês, numerados uma vez cada.
  const marcas = visiveis.map((c) => marcaDaColuna(c, r.premissas));
  const motivos: string[] = [];
  for (const m of marcas) if (!m.homologada && m.motivo && !motivos.includes(m.motivo)) motivos.push(m.motivo);

  // A altura da linha é a que a folha da casa desenha: medida na primeira linha de leitura.
  useLayoutEffect(() => {
    const tr = corpo.current?.querySelector<HTMLTableRowElement>('tr[aria-rowindex]');
    const h = tr?.getBoundingClientRect().height ?? 0;
    if (h > 0 && Math.abs(h - alturaDaLinha) > 0.5) setAlturaDaLinha(h);
  });

  const medirAJanela = () => {
    const el = caixa.current;
    if (el) setJanela({ topo: el.scrollTop, altura: el.clientHeight });
  };
  useEffect(medirAJanela, []);

  /* Na primeira vez que o cursor tem leitura (a tabela aberta direto, o F5), a tabela vai até ela, no meio. Se a
     altura medida da linha muda depois disso (a conta usou a de antes), centra mais uma vez com a medida — só uma. */
  useEffect(() => {
    const el = caixa.current;
    if (!el || ordemDoCursor === null || ordemDoCursor < 0) return;
    const antes = centrou.current;
    if (antes && (antes.deNovo || antes.altura === alturaDaLinha)) return;
    centrou.current = { altura: alturaDaLinha, deNovo: antes !== null };
    el.scrollTop = Math.max(0, ordemDoCursor * alturaDaLinha - el.clientHeight / 2);
    medirAJanela();
  }, [ordemDoCursor, alturaDaLinha]);

  const primeira = Math.max(0, Math.floor(janela.topo / alturaDaLinha) - FOLGA);
  const ultima = Math.min(nBruto, Math.ceil((janela.topo + janela.altura) / alturaDaLinha) + FOLGA);
  const linhas: number[] = [];
  for (let k = primeira; k < ultima; k++) linhas.push(k);

  const alternar = (c: ColunaDoMotor) => {
    const novas = colunas.includes(c) ? colunas.filter((x) => x !== c) : COLUNAS_ESCOLHIVEIS.filter((x) => x === c || colunas.includes(x));
    setColunas(novas);
    gravarColunas(modo, novas);
  };

  // As colunas fixas dos correlacionados ficam presas onde a tabela as desenha (medidas na cabeça, sem rolar).
  useLayoutEffect(() => {
    if (!correlacionados || !cabeca.current) return;
    // A soma das larguras das fixas anteriores (o `offsetLeft` de uma célula presa já vem deslocado por ela).
    const ths = [...cabeca.current.querySelectorAll<HTMLTableCellElement>('th.cptu-fixa')];
    let soma = 0;
    const novas = ths.map((th) => { const x = soma; soma += th.getBoundingClientRect().width; return x; });
    if (novas.length !== esquerdas.length || novas.some((x, k) => Math.abs(x - esquerdas[k]) > 0.5)) setEsquerdas(novas);
  });
  const fixa = (k: number, classe?: string) => (correlacionados
    ? { className: [classe, 'cptu-fixa'].filter(Boolean).join(' '), style: esquerdas[k] !== undefined ? { left: esquerdas[k] } : undefined }
    : { className: classe });
  const marcaDoSbtn = marcaDaColuna('sbtn', r.premissas);

  const valorDe = (c: ColunaDoMotor, i: number): string =>
    ehZona(c) ? zonaCurta(c, r[c][i]) : textoDoValor(c as Grandeza, r.col[c as Grandeza][i]);

  // A escolha das colunas também diz o porquê de cada «▲», numerado e à vista (P-UI-44).
  const escolhiveis = COLUNAS_ESCOLHIVEIS.map((c) => ({ c, m: marcaDaColuna(c, r.premissas) })).filter((x) => x.m.mostrar);
  const motivosDaEscolha: string[] = [];
  for (const { m } of escolhiveis) if (!m.homologada && m.motivo && !motivosDaEscolha.includes(m.motivo)) motivosDaEscolha.push(m.motivo);

  const total = 4 + visiveis.length;
  const notaDoSbtn = correlacionados && !marcaDoSbtn.homologada && marcaDoSbtn.motivo
    ? (motivos.includes(marcaDoSbtn.motivo) ? motivos.indexOf(marcaDoSbtn.motivo) + 1 : (motivos.push(marcaDoSbtn.motivo), motivos.length))
    : null;
  return (
    <section className="cartao cptu-tabela-vertical" aria-label="Tabela da vertical">
      <div className="cptu-tabela-barra">
        <p className="cptu-tabela-conta">
          {leituras(nBruto)} do arquivo
          {r.removidas.length > 0
            && ` · ${plural(r.removidas.length, 'tirada pela edição (riscada)', 'tiradas pela edição (riscadas)')}`}
          {' · '}{correlacionados ? 'qc e fs como foram lidos · profundidade, qc, fs e SBTn fixos à esquerda' : 'qc, fs e u₂ como foram lidos'}
        </p>
        <button type="button" className="botao secundario" aria-expanded={escolhendo} onClick={() => setEscolhendo((x) => !x)}>
          Colunas
        </button>
      </div>
      {escolhendo && (
        <fieldset className="cptu-menu-colunas">
          <legend>{correlacionados ? 'As correlações' : 'As colunas calculadas'}</legend>
          {escolhiveis.map(({ c, m }) => (
            <label key={c} className="cptu-opcao">
              <input type="checkbox" checked={colunas.includes(c)} onChange={() => alternar(c)} />
              <span className="cptu-opcao-rotulo">{cabecaDe(c)}</span>
              {!m.homologada && m.motivo && (
                <span className="cptu-marca-texto"> ▲ ({motivosDaEscolha.indexOf(m.motivo) + 1})</span>
              )}
            </label>
          ))}
          <NotasDoPorque motivos={motivosDaEscolha} />
          <button type="button" className="ligacao" onClick={() => setEscolhendo(false)}>Fechar</button>
        </fieldset>
      )}
      <div
        className={`rola congelada cptu-tabela-caixa${correlacionados ? ' cptu-tabela-correlacionados' : ''}`}
        ref={caixa}
        tabIndex={0}
        role="region"
        aria-label="As leituras da vertical (rola por dentro)"
        onScroll={medirAJanela}
      >
        <table aria-rowcount={nBruto} aria-label={correlacionados ? 'Leituras da vertical: os parâmetros correlacionados' : 'Leituras da vertical'}>
          <thead>
            <tr ref={cabeca}>
              <th scope="col" {...fixa(0, 'n')}>z (m)</th>
              <th scope="col" {...fixa(1, 'n')}>qc (MPa)</th>
              <th scope="col" {...fixa(2, 'n')}>fs (kPa)</th>
              {correlacionados ? (
                <th scope="col" {...fixa(3)}>
                  SBTn{notaDoSbtn !== null && <span className="cptu-marca-texto"> ▲ ({notaDoSbtn})</span>}
                </th>
              ) : <th scope="col" className="n">u₂ (kPa)</th>}
              {visiveis.map((c, j) => {
                const m = marcas[j];
                const nota = !m.homologada && m.motivo ? motivos.indexOf(m.motivo) + 1 : null;
                return (
                  <th key={c} scope="col" className={ehZona(c) ? undefined : 'n'}>
                    {cabecaDe(c)}
                    {nota !== null && <span className="cptu-marca-texto"> ▲ ({nota})</span>}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody ref={corpo}>
            {primeira > 0 && (
              <tr className="cptu-espaco" aria-hidden="true"><td colSpan={total} style={{ height: primeira * alturaDaLinha }} /></tr>
            )}
            {linhas.map((k) => {
              const i = mapa.doBruto[k];
              const removida = i < 0;
              const noCursor = k === ordemDoCursor;
              const classe = [removida ? 'removida' : '', noCursor ? 'no-cursor' : ''].filter(Boolean).join(' ');
              const z = numeroBr(bruto.profundidade[k], 2);
              return (
                <tr key={k} aria-rowindex={k + 1} className={classe || undefined}
                  aria-current={noCursor ? 'true' : undefined} onClick={() => aoEscolher(k)}>
                  <td {...fixa(0, 'n mono')}>
                    <button type="button" className="cptu-z-da-linha"
                      aria-label={`${z} m: levar o cursor a esta leitura`}
                      onClick={(e) => { e.stopPropagation(); aoEscolher(k); }}>
                      {z}
                    </button>
                  </td>
                  <td {...fixa(1, 'n mono')}>{textoDoValor('qc', bruto.qc[k])}</td>
                  <td {...fixa(2, 'n mono')}>{textoDoValor('fs', bruto.fs[k])}</td>
                  {correlacionados
                    ? (!removida && <td {...fixa(3)}>{zonaCurta('sbtn', r.sbtn[i])}</td>)
                    : <td className="n mono">{textoDoValor('u2', bruto.u2[k])}</td>}
                  {removida ? (
                    (visiveis.length > 0 || correlacionados) && (
                      <td colSpan={visiveis.length + (correlacionados ? 1 : 0)} className="cptu-motivo-da-removida">
                        removida: {motivoDa.get(k) ?? 'sem leitura no resultado'}
                      </td>
                    )
                  ) : visiveis.map((c) => (
                    <td key={c} className={ehZona(c) ? undefined : 'n mono'}>{valorDe(c, i)}</td>
                  ))}
                </tr>
              );
            })}
            {ultima < nBruto && (
              <tr className="cptu-espaco" aria-hidden="true"><td colSpan={total} style={{ height: (nBruto - ultima) * alturaDaLinha }} /></tr>
            )}
          </tbody>
        </table>
      </div>
      <NotasDoPorque motivos={motivos} />
    </section>
  );
}

/** As notas numeradas do «▲ não homologada»: o porquê de cada número, à vista (o `title` nunca é o único caminho). */
export function NotasDoPorque({ motivos }: { motivos: readonly string[] }) {
  if (motivos.length === 0) return null;
  return (
    <div className="cptu-notas-do-porque">
      {motivos.map((m, k) => (
        <p key={m} className="cptu-nota-do-porque">
          <span className="cptu-marca-texto">({k + 1}) {MARCA_NAO_HOMOLOGADA}</span> · {m}
        </p>
      ))}
    </div>
  );
}
